import { useTranslation } from 'react-i18next';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';

/**
 * "Discard this equation?" — the guard on the two formula editors' X and Escape.
 *
 * ── WHY ONLY TWO OF THE FOUR WAYS OUT ─────────────────────────────────────────────────────────
 * Both editors had four routes to `onClose` and none of them asked: Cancel, the X, Escape, and
 * (until it was fixed) an Escape meant for a popup that fell through to the modal. A teacher who
 * had built a long expression lost it to whichever one they hit.
 *
 * Cancel is not guarded. It names its own outcome — you press it BECAUSE you do not want the
 * changes — and a confirm on it asks the same question twice. The X and Escape mean "dismiss this
 * window", which says nothing about the work inside it, and those are the two that need the ask.
 *
 * And only when something has actually changed: an editor opened and closed untouched is the
 * common case, and making that cost a second click would be the wrong trade for everyone.
 */
export function DiscardConfirmModal({ onDiscard, onKeepEditing }: {
  onDiscard: () => void;
  onKeepEditing: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ModalShell
      title={t('modals.discardFormula.title')}
      onClose={onKeepEditing}
      maxWidth='max-w-sm'
      bottomSheetOnPhone
    >
      <div className='flex flex-col gap-6'>
        <p className='text-sm text-ws-muted'>{t('modals.discardFormula.message')}</p>
        <div className='flex gap-3'>
          {/* Keep editing is the safe one and sits where Cancel sits in every other confirm. */}
          <Button variant='secondary' onClick={onKeepEditing} className='flex-1'>
            {t('modals.discardFormula.keepEditing')}
          </Button>
          <Button variant='primary' onClick={onDiscard} className='flex-1'>
            {t('modals.discardFormula.discard')}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
